import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, Crown, PlayCircle, Star } from 'lucide-react'
import {
  getAdminUserActivity,
  getAdminUserDetail,
  type AdminUserActivityEvent,
  type AdminUserDetail,
} from '@/lib/api/admin'

export function AdminUserDetail() {
  const { userId } = useParams<{ userId: string }>()
  const [detail, setDetail] = useState<AdminUserDetail | null>(null)
  const [activity, setActivity] = useState<AdminUserActivityEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!userId) return
    let cancelled = false
    async function load() {
      try {
        setLoading(true)
        setError(null)
        const [d, a] = await Promise.all([
          getAdminUserDetail(userId),
          getAdminUserActivity(userId),
        ])
        if (cancelled) return
        setDetail(d)
        setActivity(a.items ?? [])
      } catch (err) {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load user details')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [userId])

  const lastActiveLabel =
    detail?.lastActiveAt && !Number.isNaN(Date.parse(detail.lastActiveAt))
      ? new Date(detail.lastActiveAt).toLocaleString()
      : 'Unknown'

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link
            to="/admin/users"
            className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-200"
          >
            <ArrowLeft className="h-3 w-3" />
            Back to users
          </Link>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-700 bg-red-900/30 px-3 py-2 text-xs text-red-200">
          {error}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-[minmax(0,1.5fr)_minmax(0,1.7fr)]">
        <div className="rounded-xl border border-gray-800 bg-black/40 p-4 space-y-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">User</p>
            {loading && !detail ? (
              <p className="text-sm text-gray-400">Loading…</p>
            ) : detail ? (
              <>
                <p className="text-lg font-semibold text-white">
                  {detail.displayName || detail.email}
                </p>
                <p className="text-xs text-gray-400">{detail.email}</p>
              </>
            ) : (
              <p className="text-sm text-gray-400">User not found.</p>
            )}
          </div>
          {detail && (
            <>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                {detail.isAdmin && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#D4AF37]/10 px-2 py-0.5 text-[#D4AF37]">
                    <Crown className="h-3 w-3" />
                    Admin
                  </span>
                )}
                {detail.isCreator && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-indigo-500/10 px-2 py-0.5 text-indigo-300">
                    <Star className="h-3 w-3" />
                    Creator
                  </span>
                )}
                {detail.verified && (
                  <span className="inline-flex items-center rounded-full bg-emerald-500/10 px-2 py-0.5 text-emerald-300">
                    Verified
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs text-gray-400">
                <div>
                  <p className="uppercase tracking-wide text-[11px] text-gray-500 mb-0.5">
                    Joined
                  </p>
                  <p>
                    {detail.createdAt
                      ? new Date(detail.createdAt).toLocaleDateString()
                      : 'Unknown'}
                  </p>
                </div>
                <div>
                  <p className="uppercase tracking-wide text-[11px] text-gray-500 mb-0.5">
                    Last active
                  </p>
                  <p>{lastActiveLabel}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs text-gray-400">
                <div>
                  <p className="uppercase tracking-wide text-[11px] text-gray-500 mb-0.5">
                    Reward points
                  </p>
                  <p>{detail.rewardPoints.toLocaleString()}</p>
                </div>
                <div>
                  <p className="uppercase tracking-wide text-[11px] text-gray-500 mb-0.5">
                    Subscription
                  </p>
                  {detail.subscription ? (
                    <p className="capitalize">
                      {detail.subscription.plan} · {detail.subscription.status}
                    </p>
                  ) : (
                    <p>None</p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="rounded-xl border border-gray-800 bg-black/40 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-wide text-gray-500">Recent activity</p>
          </div>
          {loading && !activity.length ? (
            <p className="text-sm text-gray-400">Loading activity…</p>
          ) : activity.length === 0 ? (
            <p className="text-sm text-gray-400">No recent activity recorded for this user.</p>
          ) : (
            <div className="space-y-2 text-xs text-gray-300 max-h-72 overflow-y-auto">
              {activity.map((event, index) => {
                const key = `${event.type}-${event.occurredAt || index}-${index}`
                if (event.type === 'view') {
                  return (
                    <div
                      key={key}
                      className="flex items-start gap-2 rounded-lg border border-gray-900 bg-black/40 px-3 py-2"
                    >
                      <PlayCircle className="h-3.5 w-3.5 text-[#D4AF37] mt-0.5" />
                      <div className="flex-1">
                        <p className="text-xs text-gray-200">
                          Watched episode{' '}
                          {event.episodeNumber != null ? `#${event.episodeNumber}` : ''}{' '}
                          {event.episodeTitle || ''}
                        </p>
                        {event.filmTitle && (
                          <p className="text-[11px] text-gray-500">Series: {event.filmTitle}</p>
                        )}
                        <p className="text-[11px] text-gray-500 mt-0.5">
                          {event.occurredAt
                            ? new Date(event.occurredAt).toLocaleString()
                            : ''}
                        </p>
                      </div>
                    </div>
                  )
                }
                if (event.type === 'reward') {
                  const sign = event.direction === 'spent' ? '-' : '+'
                  return (
                    <div
                      key={key}
                      className="flex items-start gap-2 rounded-lg border border-gray-900 bg-black/40 px-3 py-2"
                    >
                      <Star className="h-3.5 w-3.5 text-emerald-300 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-xs text-gray-200">
                          {sign}
                          {event.amount ?? 0} reward points ({event.direction})
                        </p>
                        {event.description && (
                          <p className="text-[11px] text-gray-500">{event.description}</p>
                        )}
                        <p className="text-[11px] text-gray-500 mt-0.5">
                          {event.occurredAt
                            ? new Date(event.occurredAt).toLocaleString()
                            : ''}
                        </p>
                      </div>
                    </div>
                  )
                }
                return null
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

